import React from 'react';
import { Card, CardContent, Chip, Tooltip } from '@material-ui/core';
import { SETTINGS_COLUMN_WIDTH } from './CampaignDeliveryCards';

/**
 * Why a campaign is or is not delivering, resolved from the campaign document.
 *
 * NBU-7: the advertiser campaign list previously derived its status from
 * `advertiser_limit_paused` and `active` alone, so prod campaign 1709 - stopped
 * dead by its ~$2,000 lifetime spend limit on 2026-08-16, and correctly shown
 * as paused on the ops screen - rendered a green "Delivering" chip to the
 * advertiser who owned it. A status the ad server disagrees with is worse than
 * no status at all: it sends the advertiser looking for a delivery problem that
 * is actually a budget they set themselves.
 *
 * The order below is the ad server's own gate order, from
 * Campaign.isExecutable() in adserver
 * (src/main/java/com/nobid/adserver/domain/Campaign.java:391-398):
 *
 *   1. isCampaignDisabled()        - any *_limit_active flag explicitly false
 *   2. isActive()                  - the campaign's own on/off switch
 *   3. isExecutableForFlightDates() - start_date / end_date
 *   4. isExecutableForSpendLimit()  - advertiser.advertiser_limit_paused
 *
 * Keeping that order matters for correctness, not tidiness: a campaign can trip
 * several gates at once, and the reason shown must be the one the ad server
 * actually acts on first. A campaign that is both over its lifetime budget and
 * switched off reads as budget-exhausted, because that is the gate that fires.
 *
 * Deliberately NOT modelled: day parting, pacing, frequency capping, segment
 * targeting and IP lists. Those are per-request decisions with no persisted
 * flag to read, so a campaign passing every gate here is reported as
 * DELIVERING - meaning "eligible", not "guaranteed to win an auction".
 */

// The four flags the budget coordinator ETL writes per cycle, matching
// Campaign.isCampaignDisabled() field-for-field.
//
// ORDER HERE IS PRIORITY, and it is deliberately NOT the Java method's.
// Campaign.java:601-606 is a plain OR that short-circuits to true whichever
// clause matches first, so its order carries no meaning. This list is read by
// firstExhaustedFlag, which takes the FIRST exhausted flag and names it as the
// reason - so order decides which sentence an advertiser reads.
//
// Lifetime first, because a lifetime pause is TERMINAL and a daily one is
// transient. With the daily flags first, a campaign that had exhausted both read
// "reached its daily spend limit, it resumes at midnight" - and at midnight it
// did not resume, because the lifetime cap was still closed. Naming the transient
// cause promises a recovery the ad server will not deliver, which is the same
// class of lie as the "Delivering" chip this resolver replaced.
//
// Within each pair the order is arbitrary but stable: revenue before the other
// unit, so a campaign over both reports money first.
export const BUDGET_PAUSE_FLAGS = [
  'lifetime_revenue_limit_active',
  'lifetime_impressions_limit_active',
  'daily_revenue_limit_active',
  'daily_clicks_limit_active'
];

// What each flag means to the advertiser reading it. Three facts per flag,
// because all three change what the banner should say:
//
//   label      names the cause, so the chip never says "a budget limit"
//   resets     DAILY limits reopen at local midnight, LIFETIME ones never do.
//              That is the difference between "resumes at midnight" and
//              "paused since", and it is why only the lifetime gate carries a
//              stored timestamp (insights budget_coordinator.js).
//   editable   whether the advertiser can raise it themselves. daily_clicks_limit
//              is absent from CONSOLE_CAMPAIGN_WRITABLE, so telling an advertiser
//              to raise it sends them looking for a field they will never find -
//              the same class of mistake as the old "Budget & Schedule tab".
const BUDGET_PAUSE_LABELS = {
  daily_revenue_limit_active: { label: 'daily spend limit', resets: true, editable: true },
  daily_clicks_limit_active: { label: 'daily clicks limit', resets: true, editable: false },
  lifetime_revenue_limit_active: { label: 'lifetime spend limit', resets: false, editable: true },
  lifetime_impressions_limit_active: { label: 'lifetime impressions limit', resets: false, editable: true }
};

// Mirrors resolveTimezone in insights service/etl/budget_pacing.js:38-41, which
// is what decides when a daily limit actually resets: the campaign's own
// flight_timezone, falling back to Eastern. The two must agree - a banner naming
// a different zone than the coordinator counts in would state a reset time that
// is simply wrong.
const DEFAULT_DAILY_RESET_TZ = 'America/New_York';

function resolveResetTimezone (record) {
  const tz = record && record.flight_timezone;
  if (typeof tz !== 'string' || tz.length === 0) return DEFAULT_DAILY_RESET_TZ;
  try {
    // Intl throws RangeError on an unknown zone, which is the only reliable
    // validity check available in the browser.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch (e) {
    return DEFAULT_DAILY_RESET_TZ;
  }
}

/** "Paused since Aug 16, 2026, 3:42 PM", or '' when nothing was recorded. */
function pausedSince (value) {
  if (!value) return '';
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return '';
  return ` Paused since ${new Date(ms).toLocaleString()}.`;
}

export const STATUS = {
  DELIVERING: 'delivering',
  BUDGET_REACHED: 'budget_reached',
  OFF: 'off',
  BEFORE_FLIGHT: 'before_flight',
  AFTER_FLIGHT: 'after_flight',
  ADVERTISER_LIMIT_PAUSED: 'advertiser_limit_paused'
};

/**
 * ONLY an explicit `false` is exhausted. The budget coordinator writes these
 * flags per cycle, so an ABSENT flag means "not processed yet" - unknown, not
 * paused. Testing falsiness here would flag every newly created campaign as
 * over budget. Same rule as utils.pausedIndicator and CampaignDeliveryCards,
 * which render alongside this.
 */
function firstExhaustedFlag (record) {
  return BUDGET_PAUSE_FLAGS.find(flag => record[flag] === false) || null;
}

/**
 * Flight bounds are read from `start_date`/`end_date`, NOT from
 * `flight_start_day`/`flight_end_day`.
 *
 * The day fields are timezone-less calendar days whose meaning depends on the
 * campaign's own `flight_timezone`; insights resolves them to exact UTC
 * instants on every write (service/utils/flight_dates.js applyFlightFields) and
 * the ad server compares against those instants. Reading the instants gives the
 * browser the ad server's answer for free - no timezone arithmetic here, and no
 * second implementation of it to drift.
 *
 * An unparseable or absent bound is treated as unbounded, matching the Java
 * null checks rather than failing the campaign closed on bad data.
 */
function flightBound (value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// Who is reading. It changes what is TRUE, not just what is polite: the remedy
// half of every stopped state is written to an advertiser looking at their own
// campaign, and on the ops form each of those instructions is wrong. There is no
// card titled Budget on the ops Settings tab (the limits are a table,
// adcampaigns.js:359); ops can edit daily_clicks_limit directly
// (adcampaigns.js:390), so "contact your account manager" tells them to contact
// themselves; and "your account" names an account that is not theirs.
export const AUDIENCE = {
  ADVERTISER: 'advertiser',
  OPS: 'ops'
};

/**
 * @param record campaign document (or equivalent form state)
 * @param nowMs  injectable clock, for tests
 * @returns {{status: string, label: string, detail: string|null,
 *            diagnosis: string|null, delivering: boolean}}
 *
 * `detail` is the ADVERTISER sentence - diagnosis plus remedy - used by the
 * advertiser banner and by the chip tooltip. `diagnosis` is the same cause and
 * timing with the remedy removed and the possessives neutralised, for readers
 * the remedy is not addressed to. Both are null while delivering.
 *
 * They are separate strings rather than one string with a flag because only the
 * remedy is audience-specific: which limit fired and when it closed is the same
 * fact for everyone, and it is the fact the ops form was missing entirely.
 */
export function resolveCampaignStatus (record, nowMs = Date.now()) {
  if (!record) return null;

  const exhausted = firstExhaustedFlag(record);
  if (exhausted) {
    const { label, resets, editable } = BUDGET_PAUSE_LABELS[exhausted];

    // A daily limit reopens on its own, so the useful fact is WHEN. A lifetime
    // limit never reopens, so the useful fact is when it closed plus how to
    // raise it. Nothing stored is needed for the daily case: the reset boundary
    // is derivable from the campaign's own timezone.
    const timing = resets
      ? ` It resumes at midnight ${resolveResetTimezone(record)}.`
      : pausedSince(record.lifetime_limit_paused_at);

    // Names the Budget card, not a tab: the spend limits moved onto Settings when
    // the budget fields were consolidated there, and the tabs are Settings /
    // Limits / Filters. The old copy sent advertisers looking for a
    // "Budget & Schedule" tab that does not exist on either role's form.
    //
    // An ABSOLUTE location, never a relative one. This sentence has pointed the
    // wrong way twice: first at a "Budget & Schedule" tab that never existed,
    // then "above" - when the banner renders at the TOP of Settings and the
    // Budget card sits below it, which it did from the day that copy shipped.
    //
    // Two reasons not to just flip it to "below". Where the card sits relative
    // to the banner is the form layout's business and it moves - it moved once
    // already when the performance panel went to the top. And this string is
    // ALSO the list chip's tooltip (CampaignStatusChip), where there is no
    // banner to be above or below and no tab on screen at all. Naming the tab
    // is the one phrasing that is true in both places.
    //
    // Saying WHICH tab earns its words: the tabs are Settings / Limits /
    // Filters, so Limits is where an advertiser goes looking for a spend limit,
    // and the spend limits are not there.
    //
    // And it only offers the raise when the advertiser can actually perform it.
    const remedy = editable
      ? ` Raise the ${label} in the Budget card, on the Settings tab, to resume${resets ? ' sooner' : ''}.`
      : ' Contact your account manager to raise it.';

    // detail is diagnosis + remedy, so the advertiser string stays exactly what
    // it was when the remedy was inlined here.
    const diagnosis = `Delivery stopped because this campaign reached its ${label}.${timing}`;

    return {
      status: STATUS.BUDGET_REACHED,
      label: `Paused - ${label} reached`,
      detail: `${diagnosis}${remedy}`,
      diagnosis,
      delivering: false
    };
  }

  if (!record.active) {
    return {
      status: STATUS.OFF,
      label: 'Off',
      detail: 'This campaign is switched off. Turn on Active to deliver.',
      // The Active switch exists on both forms, so this remedy is not wrong for
      // ops - just unnecessary. Ops opened the form; they can see the toggle.
      diagnosis: 'This campaign is switched off.',
      delivering: false
    };
  }

  // The two flight states carry no remedy and no possessive, so both audiences
  // read the same sentence.
  const start = flightBound(record.start_date);
  if (start !== null && nowMs < start) {
    const detail = `Delivery starts ${record.flight_start_day || new Date(start).toLocaleString()}.`;
    return {
      status: STATUS.BEFORE_FLIGHT,
      label: 'Scheduled',
      detail,
      diagnosis: detail,
      delivering: false
    };
  }

  const end = flightBound(record.end_date);
  if (end !== null && nowMs > end) {
    const detail = `The flight ended ${record.flight_end_day || new Date(end).toLocaleString()}.`;
    return {
      status: STATUS.AFTER_FLIGHT,
      label: 'Ended',
      detail,
      diagnosis: detail,
      delivering: false
    };
  }

  // Last, exactly as in isExecutable(): an account-level pause is checked only
  // after the campaign's own gates, so a campaign that is off reads as off
  // rather than blaming the account.
  if (record.advertiser_limit_paused) {
    // The timing sentence lives here rather than in the banner so the list
    // chip's tooltip carries it too - it was banner-only before, which meant
    // the same pause read differently in two places.
    const timing = pausedSince(record.advertiser_limit_paused_at)
      + ' It resumes automatically at the start of the next period.';

    return {
      status: STATUS.ADVERTISER_LIMIT_PAUSED,
      label: 'Paused - account spend limit reached',
      detail: 'Delivery stopped because your account reached its monthly spend '
        + `limit.${timing} Contact your account manager to raise the limit.`,
      // The only state whose diagnosis is not a prefix of detail: "your account"
      // is the wrong account when ops is reading someone else's campaign. The
      // account limit lives on the ADVERTISER record, not this one, so ops is
      // told which document to go look at rather than being sent to a field that
      // is not on this form at all.
      diagnosis: 'Delivery stopped because this campaign\'s advertiser reached '
        + `its monthly account spend limit.${timing}`
        + ' The limit is on the advertiser record.',
      delivering: false
    };
  }

  return {
    status: STATUS.DELIVERING,
    label: 'Delivering',
    detail: null,
    diagnosis: null,
    delivering: true
  };
}

// One colour per status. A budget pause and an account-level pause are both
// stop conditions and share the red, but they keep distinct labels: conflating
// "you spent your campaign budget" with "your account is capped" is how support
// tickets get opened.
const STATUS_STYLES = {
  [STATUS.DELIVERING]: { backgroundColor: '#2e7d32', color: '#fff' },
  [STATUS.BUDGET_REACHED]: { backgroundColor: '#c62828', color: '#fff' },
  [STATUS.ADVERTISER_LIMIT_PAUSED]: { backgroundColor: '#c62828', color: '#fff' },
  [STATUS.BEFORE_FLIGHT]: { backgroundColor: '#1565c0', color: '#fff' },
  // Off and Ended are neutral states, not failures - MUI's default Chip.
  [STATUS.OFF]: undefined,
  [STATUS.AFTER_FLIGHT]: undefined
};

/**
 * The status chip, with the reason as its tooltip.
 *
 * `audience` behaves as it does on the banner: ops gets the diagnosis without
 * the advertiser remedy, which on an ops screen points at a Budget card that
 * form does not have and at an account manager who is the reader.
 */
export const CampaignStatusChip = ({ record, nowMs, audience = AUDIENCE.ADVERTISER }) => {
  const state = resolveCampaignStatus(record, nowMs);
  if (!state) return null;
  const chip = <Chip size="small" label={state.label} style={STATUS_STYLES[state.status]} />;
  const tooltip = audience === AUDIENCE.OPS ? state.diagnosis : state.detail;
  // A Tooltip needs a single child that can hold a ref; Chip can, but wrapping
  // unconditionally would attach an empty tooltip to every delivering campaign.
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

// The banner pins BOTH colours, and the text colour is the load-bearing half.
//
// It was background-only, so the text came from the MUI theme - which in the dark
// theme is near-white, on this deliberately light alert background. The banner
// rendered as a pink bar with invisible text: seen in prod on campaign 1709,
// legible only by selecting it. A hard-coded background must always carry a
// hard-coded foreground, the same pairing TrafficPanel.js uses for its light
// preview panes.
//
// #611a15 is MUI v4's own error text colour for this background, so the pair
// stays a recognisable alert rather than an invented palette. Hoisted to module
// scope for identity stability across renders.
//
// WIDTH IS THE SHARED COLUMN, not 100%. Full-bleed, this rendered as a pink slab
// spanning the whole monitor directly above a 1088px centred performance panel -
// the widest, loudest element on the screen carrying one sentence. It measures
// from the same constant the panel does, so the three stacked pieces (banner,
// chart, cards) share one left and right edge at every window size.
const BANNER_STYLE = {
  backgroundColor: '#fdecea',
  color: '#611a15',
  width: SETTINGS_COLUMN_WIDTH,
  // Narrow viewports win, matching the panel: shrink rather than scroll.
  maxWidth: '100%',
  margin: '0 auto 12px',
  // Carries the alert at the edge so the fill itself can stay pale - the reason
  // the pale fill is safe to keep at this reduced size.
  borderLeft: '4px solid #c62828'
};

// CardContent's default padding is 24px, and 32px on the bottom via its
// :last-child rule, which made a one-sentence alert three lines tall. An inline
// style beats both. The banner is an interruption, not a section - it should cost
// the vertical space of the sentence it contains and no more.
const BANNER_CONTENT_STYLE = {
  padding: '10px 16px',
  fontSize: '13px',
  lineHeight: 1.5
};

/**
 * The same reason as a banner, for the campaign form where there is room to
 * explain. Renders nothing while the campaign is delivering.
 *
 * Replaces NBU-5's SpendLimitPausedBanner, which explained exactly one of the
 * six states and left the other four stopped states unexplained.
 *
 * `audience` defaults to advertiser, so the advertiser form needs no change and
 * gets exactly the string it had. The ops form passes AUDIENCE.OPS, which drops
 * the remedy: it was mounted on the advertiser form only, which left the ops
 * form showing PAUSED chips and no way to tell WHICH limit closed or WHEN - the
 * when being a field (lifetime_limit_paused_at) added in the same change that
 * only ever surfaced on one of the two screens showing campaigns.
 */
export const CampaignStatusBanner = ({ record, nowMs, audience = AUDIENCE.ADVERTISER }) => {
  const state = resolveCampaignStatus(record, nowMs);
  if (!state || state.delivering) return null;
  const body = audience === AUDIENCE.OPS ? state.diagnosis : state.detail;
  if (!body) return null;
  return (
    // Not `raised`: elevation 8 under a pale fill read as a floating slab. The
    // left rule carries the alert instead.
    <Card style={BANNER_STYLE}>
      <CardContent style={BANNER_CONTENT_STYLE}>
        <strong>{state.label}.</strong> {body}
      </CardContent>
    </Card>
  );
};
